import type { AxiosResponse } from 'axios'
import type { NSnackbarProps } from '@/modules/global/components/core/NSnackbar/NSnackbar.vue'
import Space, { DigitalSignatureStatus, SpaceStatus } from '@/modules/space/models/Space'

export interface PdfGenerationResult {
  downloaded: boolean
  finalizationPending: boolean
  finalizationMessage: string | null
  code: string | null
}

export enum SpacePdfDownloadUiState {
  READY = 'READY',
  PROCESSING = 'PROCESSING'
}

export const PDF_FINALIZATION_ERROR_CODES = new Set(['pdf-finalization-started', 'pdf-finalization-in-progress'])

type TeamDigitalSignatureSetting = {
  enableDigitalSignature?: boolean
}

/** `TeamStore.currentTeam` is null when the team fetch failed during boot; callers pass it through unguarded. */
type MaybeTeamDigitalSignatureSetting = TeamDigitalSignatureSetting | null | undefined

type TranslateFn = (key: string) => string

export interface SnackbarStoreLike {
  enqueue: (props: NSnackbarProps) => string
  dequeue: (id: string) => void
}

export interface ExecuteSpacePdfDownloadOptions {
  space: Space
  team: MaybeTeamDigitalSignatureSetting
  downloadFn: () => Promise<PdfGenerationResult>
  refreshSpace: (spaceId: string) => Promise<void>
  markFinalizationPending?: (spaceId: string) => void
  snackbarStore: SnackbarStoreLike
  t: TranslateFn
  onDownloaded?: () => void
}

let pdfDownloadInFlightSpaceId: string | null = null

export function resetPdfDownloadInFlightForTests(): void {
  pdfDownloadInFlightSpaceId = null
}

export function resolvePdfFinalizationMessage(data: unknown): string | null {
  if (data == null) {
    return null
  }
  if (typeof data === 'string' && data.trim()) {
    return data
  }
  if (typeof data === 'object' && data !== null && 'message' in data) {
    const m = (data as { message: unknown }).message
    if (typeof m === 'string' && m.trim()) {
      return m
    }
  }
  return null
}

export function resolvePdfFinalizationCode(data: unknown): string | null {
  if (data == null || typeof data !== 'object') {
    return null
  }
  const code = (data as { code?: unknown }).code
  return typeof code === 'string' && code.trim() ? code : null
}

/** Parses the `{ code, description }` / `{ message }` shape shared by the backend's ErrorBody responses. */
export function resolveApiErrorCode(error: unknown): string | null {
  const err = error as { response?: { data?: unknown } }
  const data = err.response?.data
  if (data == null || typeof data !== 'object') {
    return null
  }
  const code = (data as { code?: unknown }).code
  return typeof code === 'string' && code.trim() ? code : null
}

/** Parses the `{ description }` / `{ message }` shape shared by the backend's ErrorBody responses. */
export function resolveApiErrorDescription(error: unknown): string | null {
  const err = error as { response?: { data?: unknown } }
  const data = err.response?.data
  if (data == null || typeof data !== 'object') {
    return null
  }
  const body = data as { description?: unknown; message?: unknown }
  if (typeof body.description === 'string' && body.description.trim()) {
    return body.description
  }
  if (typeof body.message === 'string' && body.message.trim()) {
    return body.message
  }
  return null
}

export function isPdfFinalizationEligible(space: Space, team: MaybeTeamDigitalSignatureSetting): boolean {
  return space.status === SpaceStatus.FULLY_SIGNED && !!team?.enableDigitalSignature
}

export function deriveSpacePdfDownloadUiState(
  space: Space,
  team: MaybeTeamDigitalSignatureSetting
): SpacePdfDownloadUiState {
  if (!isPdfFinalizationEligible(space, team)) {
    return SpacePdfDownloadUiState.READY
  }

  const status = (space.digitalSignatureStatus?.trim() || DigitalSignatureStatus.NOT_STARTED) as DigitalSignatureStatus
  if (status === DigitalSignatureStatus.PENDING || status === DigitalSignatureStatus.PROCESSING) {
    return SpacePdfDownloadUiState.PROCESSING
  }

  return SpacePdfDownloadUiState.READY
}

export function isSpacePdfDownloadProcessing(
  space: Space | null | undefined,
  team: MaybeTeamDigitalSignatureSetting
): boolean {
  if (!space || !team) {
    return false
  }
  return deriveSpacePdfDownloadUiState(space, team) === SpacePdfDownloadUiState.PROCESSING
}

export function resolvePdfFinalizationErrorMessage(error: unknown): string | null {
  return resolveApiErrorDescription(error)
}

export function resolvePdfDownloadErrorMessage(
  error: unknown,
  fallback: string,
  options: { finalizationEligible: boolean; finalizationFallback: string }
): string {
  const code = resolveApiErrorCode(error)
  if (code != null && PDF_FINALIZATION_ERROR_CODES.has(code)) {
    if (!options.finalizationEligible) {
      return fallback
    }
    return resolveApiErrorDescription(error) ?? options.finalizationFallback
  }
  return resolveApiErrorDescription(error) ?? fallback
}

/** Same body parsing as finalize errors — for GET /spaces/.../pdf and .../pages/.../pdf failures. */
export function resolvePdfDownloadErrorMessageOrFallback(error: unknown, fallback: string): string {
  return resolvePdfFinalizationErrorMessage(error) ?? fallback
}

/**
 * Interprets a PDF-endpoint Axios response: 202 means finalization is pending,
 * any other success status triggers the download via the returned URL.
 */
export function handlePdfResponse(response: AxiosResponse): PdfGenerationResult {
  if (response.status === 202) {
    return {
      downloaded: false,
      finalizationPending: true,
      finalizationMessage: resolvePdfFinalizationMessage(response.data),
      code: resolvePdfFinalizationCode(response.data)
    }
  }
  const url = typeof response.data === 'string' ? response.data : (response.data as { url: string }).url
  window.location.href = url
  return {
    downloaded: true,
    finalizationPending: false,
    finalizationMessage: null,
    code: null
  }
}

interface EnterPdfFinalizationPendingOptions {
  spaceId: string
  message: string
  refreshSpace: (spaceId: string) => Promise<void>
  markFinalizationPending?: (spaceId: string) => void
  snackbarStore: SnackbarStoreLike
}

/** Refresh store state and show a deduped snackbar so download UI enters PROCESSING. */
export async function enterPdfFinalizationPending(options: EnterPdfFinalizationPendingOptions): Promise<void> {
  const { spaceId, message, refreshSpace, markFinalizationPending, snackbarStore } = options
  try {
    await refreshSpace(spaceId)
  } catch {
    // Backend already indicated finalization is pending; still drive local UI into PROCESSING.
  }
  markFinalizationPending?.(spaceId)
  snackbarStore.enqueue({
    label: message,
    leadingIcon: true,
    variant: 'warning',
    dedupeKey: `pdf-finalization-pending-${spaceId}`
  })
}

export async function executeSpacePdfDownload(options: ExecuteSpacePdfDownloadOptions): Promise<void> {
  const { space, team, downloadFn, refreshSpace, markFinalizationPending, snackbarStore, t, onDownloaded } = options
  const finalizationEligible = isPdfFinalizationEligible(space, team)
  const generationFailedLabel = t('signatureList.pdfGenerationFailed')
  const finalizationFallbackLabel = t('signatureList.pdfFinalizationInProgress')

  if (pdfDownloadInFlightSpaceId === space.id) {
    return
  }

  if (deriveSpacePdfDownloadUiState(space, team) === SpacePdfDownloadUiState.PROCESSING) {
    return
  }

  pdfDownloadInFlightSpaceId = space.id
  const loadingSnackbarId = snackbarStore.enqueue({
    label: t('signatureList.pdfGenerationActive'),
    showLoadingIndicator: true,
    variant: 'success',
    autoHide: false
  })

  try {
    const result = await downloadFn()
    if (result.downloaded) {
      snackbarStore.enqueue({
        label: t('signatureList.pdfDownloadingNow'),
        leadingIcon: true,
        variant: 'success'
      })
      onDownloaded?.()
      return
    }

    if (result.finalizationPending) {
      if (finalizationEligible) {
        await enterPdfFinalizationPending({
          spaceId: space.id,
          message: result.finalizationMessage || finalizationFallbackLabel,
          refreshSpace,
          markFinalizationPending,
          snackbarStore
        })
        return
      }

      snackbarStore.enqueue({
        label: generationFailedLabel,
        leadingIcon: true,
        variant: 'warning'
      })
    }
  } catch (error) {
    const code = resolveApiErrorCode(error)
    if (code != null && PDF_FINALIZATION_ERROR_CODES.has(code) && finalizationEligible) {
      await enterPdfFinalizationPending({
        spaceId: space.id,
        message: resolveApiErrorDescription(error) ?? finalizationFallbackLabel,
        refreshSpace,
        markFinalizationPending,
        snackbarStore
      })
      return
    }

    snackbarStore.enqueue({
      label: resolvePdfDownloadErrorMessage(error, generationFailedLabel, {
        finalizationEligible,
        finalizationFallback: finalizationFallbackLabel
      }),
      leadingIcon: true,
      variant: 'warning'
    })
  } finally {
    snackbarStore.dequeue(loadingSnackbarId)
    if (pdfDownloadInFlightSpaceId === space.id) {
      pdfDownloadInFlightSpaceId = null
    }
  }
}
